import React, { useState, useEffect } from 'react';
import ReactEcharts from 'echarts-for-react';
import echarts from 'echarts';

import { Button, Div, Loader } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { SfBayareaData, NyTristateData, ChicagoData, DallasData } from 'src/data/zip_data';

const fetchData = async (path) => {
	const response = await fetch(`https://demo.pinpoint.ai/downloads/${path}.json`, {
		method: 'GET',
	});
	const data = await response.json();
	return data;
};

function Choropleth() {
	const [zipMap, setZipMap] = useState('SF_BAYAREA');
	const [zipData, setZipData] = useState(SfBayareaData);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function f() {
			if (!echarts.getMap('SF_BAYAREA')) {
				const sfBayareaGeo = await fetchData('sf_bayarea_geo');
				echarts.registerMap('SF_BAYAREA', sfBayareaGeo);
			}
			if (!echarts.getMap('NY_TRISTATE')) {
				const nyTristateGeo = await fetchData('ny_tristate_geo');
				echarts.registerMap('NY_TRISTATE', nyTristateGeo);
			}
			if (!echarts.getMap('CHICAGO')) {
				const chicagoGeo = await fetchData('chicago_geo');
				echarts.registerMap('CHICAGO', chicagoGeo);
			}
			if (!echarts.getMap('DALLAS')) {
				const dallasGeo = await fetchData('dallas_geo');
				echarts.registerMap('DALLAS', dallasGeo);
			}
			setIsLoading(false);
		}
		f();
	}, []);

	if (isLoading) {
		return (
			<Div w="100%" h="100%" d="flex" justify="center" align="center">
				<Loader />
			</Div>
		);
	}

	return (
		<Wrapper>
			<Div d="flex" align="center" justify="flex-start" h="2.5rem" m={{ b: '1.5rem', x: '-0.5rem' }}>
				<Button
					bg={zipMap === 'SF_BAYAREA' ? 'selected' : 'brand500'}
					m={{ x: '0.5rem' }}
					onClick={() => {
						setZipMap('SF_BAYAREA');
						setZipData(SfBayareaData);
					}}
				>
					SAN FRANCISCO BAY AREA
				</Button>
				<Button
					bg={zipMap === 'NY_TRISTATE' ? 'selected' : 'brand500'}
					m={{ x: '0.5rem' }}
					onClick={() => {
						setZipMap('NY_TRISTATE');
						setZipData(NyTristateData);
					}}
				>
					NY TRI-STATE
				</Button>
				<Button
					bg={zipMap === 'CHICAGO' ? 'selected' : 'brand500'}
					m={{ x: '0.5rem' }}
					onClick={() => {
						setZipMap('CHICAGO');
						setZipData(ChicagoData);
					}}
				>
					CHICAGO
				</Button>
				<Button
					bg={zipMap === 'DALLAS' ? 'selected' : 'brand500'}
					m={{ x: '0.5rem' }}
					onClick={() => {
						setZipMap('DALLAS');
						setZipData(DallasData);
					}}
				>
					DALLAS
				</Button>
			</Div>
			<Div h="calc(100% - 4rem)" rounded="md" bg="white">
				<ReactEcharts
					style={{
						width: '100%',
						height: '100%', // 2.5rem(Button) + .5rem(Margin)
					}}
					notMerge
					option={{
						tooltip: {
							trigger: 'item',
							showDelay: 0,
							transitionDuration: 0,
							formatter(params) {
								let value = params.value.split('.');
								value = value[0].replace(/(\d{1,3})(?=(?:\d{3})+(?!\d))/g, '$1,');
								return `${params.name}: ${value}`;
							},
						},
						toolbox: {
							show: true,
							right: 20,
							top: 20,
							feature: {
								saveAsImage: {
									show: true,
									name: 'Onpoint_zip_code_rank_chart',
									title: 'download',
									iconStyle: {
										emphasis: {
											textAlign: 'right',
										},
									},
								},
							},
						},
						visualMap: {
							precision: 1,
							selectedMode: 'multiple',
							showLabel: true,
							left: '32px',
							top: 100,
							min: Math.min(...zipData.map(({ value }) => value)),
							max: Math.max(...zipData.map(({ value }) => value)),
							inRange: {
								color: [
									'#2b5c8a',
									'#316596',
									'#3d70a1',
									'#4b7dab',
									'#5689b7',
									'#5689b7',
									'#6196c3',
									'#6da2cc',
									'#7cb1d6',
									'#8bbfdd',
									'#9bcae3',
									'#d9d5c9',
									'#febf6e',
									'#fcac4f',
									'#f8983a',
									'#f2822b',
									'#ea6f22',
									'#de6220',
									'#cf5721',
									'#bf4e22',
									'#ae4523',
									'#9e3d22',
								],
							},
							text: ['High', 'Low'],
							calculable: true,
							hoverLink: false,
						},
						series: [
							{
								type: 'map',
								zoom: 1,
								roam: true,
								map: zipMap,
								label: {
									show: false,
									emphasis: {
										show: false,
									},
								},
								data: zipData.map(({ name, value }) => ({ name, value })),
								tooltip: {
									formatter: ({ dataIndex }) => {
										const { name, value, users, city, state, stateCode } = zipData[dataIndex];
										return `Zip Code: ${name}
									<br />
									Users: ${users}
									<br />
									Avg. Score: ${value}
									<br />
									City: ${city}, ${stateCode}
									<br />
									State: ${state}
									`;
									},
								},
							},
						],
					}}
				/>
			</Div>
		</Wrapper>
	);
}

export default Choropleth;
